/**
 * Environment Service
 * Handles environment-based org management logic
 */

import { supabase } from './supabaseClient';
import {
  isEnvironmentPlan,
  getStandaloneOrgLimit,
  getMaxOrgsPerEnvironment,
  LIMIT_MESSAGES
} from '../config/environmentLimits';

export interface Environment {
  id: string;
  account_id: string;
  production_org_id: string;
  org_count: number;
  max_orgs_per_environment: number;
  primary_org_id: string | null;
  primary_org_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConnectionCheckResult {
  canConnect: boolean;
  reason?: string;
  environment_id?: string;
  is_new_environment: boolean;
  org_count_in_environment?: number;
}

/**
 * Check if user can connect a new org
 */
export async function checkCanConnectOrg(
  accountId: string,
  subscriptionPlan: string | null,
  isSandbox: boolean,
  productionOrgId: string
): Promise<ConnectionCheckResult> {
  // For non-environment plans (Starter, Free), check standalone org limit
  if (!isEnvironmentPlan(subscriptionPlan)) {
    const orgLimit = getStandaloneOrgLimit(subscriptionPlan);
    const { count: currentOrgCount } = await supabase
      .from('sf_orgs')
      .select('id', { count: 'exact', head: true })
      .eq('account_id', accountId);

    if ((currentOrgCount || 0) >= orgLimit) {
      return {
        canConnect: false,
        reason: LIMIT_MESSAGES.STARTER_ORG_LIMIT_REACHED,
        is_new_environment: false
      };
    }

    return {
      canConnect: true,
      is_new_environment: false
    };
  }

  // For environment-enabled plans (Professional+)
  // Check if org belongs to existing environment
  const { data: existingEnvironment } = await supabase
    .from('account_environments')
    .select('*')
    .eq('account_id', accountId)
    .eq('production_org_id', productionOrgId)
    .single();

  if (existingEnvironment) {
    // Joining existing environment - check if it has room
    if (existingEnvironment.org_count >= existingEnvironment.max_orgs_per_environment) {
      return {
        canConnect: false,
        reason: LIMIT_MESSAGES.ENVIRONMENT_ORG_LIMIT_REACHED,
        environment_id: existingEnvironment.id,
        is_new_environment: false,
        org_count_in_environment: existingEnvironment.org_count
      };
    }

    return {
      canConnect: true,
      environment_id: existingEnvironment.id,
      is_new_environment: false,
      org_count_in_environment: existingEnvironment.org_count
    };
  }

  // Would create new environment - check environment limit
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    return {
      canConnect: false,
      reason: 'Authentication required',
      is_new_environment: true
    };
  }

  const limitResponse = await fetch('/api/subscription/get-environment-limit', {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!limitResponse.ok) {
    console.error('Failed to get environment limit from API');
    return {
      canConnect: false,
      reason: 'Failed to verify environment limit',
      is_new_environment: true
    };
  }

  const { totalLimit: environmentLimit } = await limitResponse.json();

  const { data: currentEnvironmentCount } = await supabase
    .rpc('get_account_environment_count', { p_account_id: accountId });

  if ((currentEnvironmentCount || 0) >= (environmentLimit || 0)) {
    return {
      canConnect: false,
      reason: LIMIT_MESSAGES.ENVIRONMENT_LIMIT_REACHED,
      is_new_environment: true
    };
  }

  return {
    canConnect: true,
    is_new_environment: true,
    org_count_in_environment: 0
  };
}

/**
 * Get all environments for an account
 */
export async function getAccountEnvironments(accountId: string): Promise<Environment[]> {
  const { data, error } = await supabase
    .from('account_environments')
    .select('*')
    .eq('account_id', accountId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching environments:', error);
    return [];
  }

  return data || [];
}

/**
 * Get orgs grouped by environment
 */
export async function getOrgsGroupedByEnvironment(accountId: string) {
  // Get all orgs for account
  const { data: orgs, error: orgsError } = await supabase
    .from('sf_orgs')
    .select('*')
    .eq('account_id', accountId)
    .order('created_at', { ascending: true });

  if (orgsError) {
    console.error('Error fetching orgs:', orgsError);
    return { environments: [], standaloneOrgs: [] };
  }

  // Get all environments
  const environments = await getAccountEnvironments(accountId);

  // Group orgs by environment
  const grouped = environments.map(env => ({
    environment: env,
    orgs: (orgs || []).filter(org => org.environment_id === env.id)
  }));

  // Get standalone orgs (no environment)
  const standaloneOrgs = (orgs || []).filter(org => !org.environment_id);

  return {
    environments: grouped,
    standaloneOrgs
  };
}

/**
 * Get environment limits and usage for display
 */
export async function getEnvironmentLimitsInfo(accountId: string, subscriptionPlan: string | null) {
  if (!isEnvironmentPlan(subscriptionPlan)) {
    // Standalone org limit for Starter/Free
    const orgLimit = getStandaloneOrgLimit(subscriptionPlan);
    const { count: currentOrgCount } = await supabase
      .from('sf_orgs')
      .select('id', { count: 'exact', head: true })
      .eq('account_id', accountId);

    return {
      isEnvironmentPlan: false,
      currentCount: currentOrgCount || 0,
      limit: orgLimit,
      displayText: `${currentOrgCount || 0}/${orgLimit} org${orgLimit === 1 ? '' : 's'}`
    };
  }

  // Environment limits for Professional+
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  let environmentLimit = 0;

  if (token) {
    try {
      const limitResponse = await fetch('/api/subscription/get-environment-limit', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (limitResponse.ok) {
        const limitData = await limitResponse.json();
        environmentLimit = limitData.totalLimit || 0;
      }
    } catch (error) {
      console.error('Failed to get environment limit:', error);
    }
  }

  const { data: currentEnvironmentCount } = await supabase
    .rpc('get_account_environment_count', { p_account_id: accountId });

  return {
    isEnvironmentPlan: true,
    currentCount: currentEnvironmentCount || 0,
    limit: environmentLimit || 0,
    displayText: `${currentEnvironmentCount || 0}/${environmentLimit || 0} environment${(environmentLimit || 0) === 1 ? '' : 's'}`,
    maxOrgsPerEnvironment: getMaxOrgsPerEnvironment()
  };
}

/**
 * Associate org with environment (called after org is created in sf_orgs)
 */
export async function associateOrgWithEnvironment(
  accountId: string,
  orgId: string,
  productionOrgId: string,
  isSandbox: boolean,
  orgName: string
): Promise<{ success: boolean; environmentId?: string; error?: string }> {
  try {
    const { data: environmentId, error } = await supabase
      .rpc('find_or_create_environment', {
        p_account_id: accountId,
        p_org_id: orgId,
        p_production_org_id: productionOrgId,
        p_is_sandbox: isSandbox,
        p_org_name: orgName
      });

    if (error) {
      console.error('Error associating org with environment:', error);
      return { success: false, error: error.message };
    }

    // Update sf_orgs with environment_id and is_primary flag
    const { data: envData } = await supabase
      .from('account_environments')
      .select('primary_org_id')
      .eq('id', environmentId)
      .single();

    const isPrimary = envData?.primary_org_id === orgId;

    await supabase
      .from('sf_orgs')
      .update({
        environment_id: environmentId,
        production_org_id: productionOrgId,
        is_primary_environment_org: isPrimary
      })
      .eq('id', orgId);

    return { success: true, environmentId };
  } catch (error) {
    console.error('Error in associateOrgWithEnvironment:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}
